import { action, makeObservable, observable } from 'mobx';

import type { Directories } from '../directories/directories.store';

import { SystemSettingsApi } from './system-settings.api';

export type TSettingsSections = 'palette';

export class SystemSettingsService {
  private readonly api: SystemSettingsApi;

  private settings: Partial<Record<TSettingsSections, object>> = {};

  @observable section: string | null = null;
  @observable subSection: string | null = null;

  constructor(directories: Directories) {
    this.api = new SystemSettingsApi(directories);

    makeObservable(this);
  }

  @action.bound
  setSection(section: string | null) {
    this.section = section;
  }

  @action.bound
  setSubSection(subSection: string | null) {
    this.subSection = subSection;
  }

  async loadSettings<T extends object = object>(settingsSection: TSettingsSections): Promise<T | null> {
    if (this.settings[settingsSection]) {
      return this.settings[settingsSection] as T;
    }

    try {
      const settings =
        (
          await (async () => {
            const sett = await this.api.loadSettings<T>(settingsSection);

            if (!sett.length) {
              const data = await this.api.createSetting<T>(settingsSection, {} as T);
              return [data];
            }

            return sett;
          })()
        )?.[0] ?? null;

      this.settings[settingsSection] = settings;

      return settings;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async updateSettings<T extends object = object>(
    section: TSettingsSections,
    settingsId: number,
    updatedData: T,
  ): Promise<T | null> {
    try {
      const settings = (await this.api.updateSettings<T>(section, settingsId, updatedData))?.[0] ?? null;

      this.settings[section] = settings;

      return settings;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  init() {}
}
